import { hasConnectedMcpStatus, normalizeMCPUrl } from '@littlebird/mcp';
import { ChatStatus, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { FileDropZone } from '@/components/blocks/FileDropZone';
import { Composer } from '@/components/blocks/Thread/Composer';
import { ComposerBanner } from '@/components/blocks/Thread/ComposerBanner';
import { ModalsManager } from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { DynamicBanners } from '@/features/chat/banners/dynamic-banner';
import {
  FirstRunEmptyBanner,
  FirstRunLearningBanner,
  firstRunSummaryStore,
} from '@/features/context-collection';
import { useFeedComposer } from '@/features/feed/use-feed-composer';
import { useFeedComposerEnabled } from '@/features/feed/use-feed-composer-enabled';
import { AddIntegrationsModal } from '@/features/integrations/add-integrations-modal';
import {
  useMCPCatalogQuery,
  useMCPServersQuery,
} from '@/features/mcp/mcp.query';
import { NewChatReferralCard } from '@/features/referral';
import {
  PaymentFailedNotification,
  TrialEndingNotification,
  isOutOfCreditsBlocking,
} from '@/features/subscription';
import { useHashRoute } from '@/hooks/useHashRoute';
import { useElementSize, useResizeObserver } from '@/hooks/useResizeObserver';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { useIntegrationState } from '@/integrations/useIntegrationState';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { trackEvent } from '@/utils/analytics';
import { inElectron } from '@/utils/capabilities';
import { NEW_CHAT_ID } from '@/utils/constants';
import { oncePerArgs } from '@/utils/jsUtils';

import { FEATURES, hasFeature } from '../feature-flags';

import { COMPOSER_START_NEW_THREAD, localChatEventsBus } from './chat.events';
import {
  areSuggestionChipsVisible,
  useGreeting,
  useNewChatSuggestions,
} from './chat.hooks';
import { chatStore } from './chat.store';

const HomeScreen = lazy(() =>
  import('@/features/home').then((module) => ({
    default: module.HomeScreen,
  }))
);

type FirstRunPhase = (typeof firstRunSummaryStore)['phase'];

const trackMessageInputStartedEvent = oncePerArgs((chatId: string) =>
  trackEvent('MESSAGE_INPUT_STARTED', {
    chat_id: chatId,
  })
);

export const NewChat = observer(() => {
  const { threads: threadsStore, drafts: draftsStore } = useStores();
  const feedComposerEnabled = useFeedComposerEnabled();
  const onThreadCreated = useFeedComposer();

  const threadId = NEW_CHAT_ID;
  const thread = threadsStore.getById(threadId);
  const draft = draftsStore.getDraft(threadId);
  const isStreaming = thread?.status === ChatStatus.Streaming;
  const isThinking = thread?.status === ChatStatus.Thinking;
  const isBusy = isStreaming || isThinking;

  const prevIsBusyRef = useRef(isBusy);

  const isDraggingFile = uiStore.isDraggingFile;
  const setIsDraggingFile = (value: boolean) =>
    uiStore.setIsDraggingFile(value);

  const [messageBoxRef] = useResizeObserver<HTMLDivElement>();

  const { greeting, canCycle, cycleGreeting, debugLabel } = useGreeting();
  const chipsVisible = areSuggestionChipsVisible();

  useEffect(() => {
    if (!draft?.content?.length) return;
    trackMessageInputStartedEvent(threadId);
  }, [threadId, draft?.content?.length]);

  useEffect(() => {
    if (prevIsBusyRef.current === true && isBusy === false) {
      trackEvent('MESSAGE_COMPLETED');
    }
    prevIsBusyRef.current = isBusy;
  }, [isBusy]);

  // Learning/empty outcome shows here; the ready state shows in the sidebar coachmark.
  const firstRunPhase = inElectron() ? firstRunSummaryStore.phase : 'idle';
  const firstRunBannerVisible =
    firstRunPhase === 'learning' || firstRunPhase === 'empty';

  return (
    <div className="relative h-main-outlet max-h-main-outlet w-full">
      <FileDropZone show={isDraggingFile} />
      <XStack className="h-full">
        <div className="relative flex-1 flex flex-col min-w-0">
          <div className="peer shrink-0 pt-[var(--panel-header-height)] empty:hidden">
            <PaymentFailedNotification className="mx-auto w-full max-w-2xl shrink-0 px-12 pt-4" />
            <TrialEndingNotification className="mx-auto w-full max-w-2xl shrink-0 px-12 pt-4" />
            <DynamicBanners />
          </div>
          <Suspense fallback={null}>
            <HomeScreen
              greeting={greeting}
              canCycleGreeting={canCycle}
              greetingDebugLabel={debugLabel}
              onCycleGreeting={cycleGreeting}
              isDraggingFile={isDraggingFile}
              isFirstRun={firstRunBannerVisible}
              composer={
                <NewChatComposer
                  messageBoxRef={messageBoxRef}
                  threadId={threadId}
                  setIsDraggingFile={setIsDraggingFile}
                  firstRunPhase={firstRunPhase}
                  onThreadCreated={
                    feedComposerEnabled ? onThreadCreated : undefined
                  }
                />
              }
              recommendations={chipsVisible ? <Recommendations /> : null}
              banner={
                !firstRunBannerVisible ? (
                  <YStack className="items-center">
                    <NewChatReferralCard />
                  </YStack>
                ) : (
                  <FirstRunBanner firstRunPhase={firstRunPhase} />
                )
              }
            />
          </Suspense>
        </div>
      </XStack>
    </div>
  );
});

type NewChatComposerProps = {
  messageBoxRef: ReturnType<typeof useResizeObserver<HTMLDivElement>>[0];
  threadId: string;
  setIsDraggingFile: (isDraggingFile: boolean) => void;
  firstRunPhase: FirstRunPhase;
  onThreadCreated?: (threadId: string) => void;
};

const NewChatComposer = ({
  messageBoxRef,
  threadId,
  setIsDraggingFile,
  firstRunPhase,
  onThreadCreated,
}: NewChatComposerProps) => (
  <YStack ref={messageBoxRef} className="mx-auto w-full shrink-0 gap-3">
    <div className="relative">
      <div className="relative z-10">
        <Composer
          isNewThread
          focusOnType
          threadId={threadId}
          onThreadCreated={onThreadCreated}
          setIsDraggingFile={setIsDraggingFile}
          hideCostFooter
        />
      </div>
      {firstRunPhase !== 'learning' && <ConnectIntegrationsBanner />}
    </div>
  </YStack>
);

const FirstRunBanner = ({ firstRunPhase }: { firstRunPhase: FirstRunPhase }) =>
  firstRunPhase === 'learning' ? (
    <FirstRunLearningBanner />
  ) : (
    <FirstRunEmptyBanner />
  );

/**
 * Banner width spoken for before any badge fits: paddings, the label, row
 * gap, and the dismiss button. The text matters more than the badges.
 */
const BADGE_RESERVED_PX = 300;
/** One size-4 badge plus the gap-3 after it. */
const BADGE_STEP_PX = 28;

const ConnectIntegrationsBanner = observer(
  ({ className }: { className?: string }) => {
    const { ref: bannerRef, width: bannerWidth } =
      useElementSize<HTMLDivElement>();
    const isDismissed = chatStore.connectIntegrationsBannerDismissed;
    const mcpEnabled = hasFeature(FEATURES.MCP_INTEGRATIONS);
    const { integrationStates } = useIntegrationState();

    const inactiveIntegrations = integrationStates.filter(
      (state) => !state.isConnected && state.info.featured
    );

    const shouldFetchMCP = mcpEnabled && !isDismissed;
    const { data: catalogEntries } = useMCPCatalogQuery(shouldFetchMCP);
    const { data: mcpServers } = useMCPServersQuery(shouldFetchMCP);
    const connectedMCPUrls = useMemo(() => {
      if (!mcpServers) return new Set<string>();
      return new Set(
        mcpServers
          .filter(hasConnectedMcpStatus)
          .map((s) => normalizeMCPUrl(s.url))
      );
    }, [mcpServers]);
    const unconnectedCatalog = useMemo(() => {
      if (!mcpEnabled || !catalogEntries) return [];
      return catalogEntries.filter(
        (entry) =>
          entry.featured && !connectedMCPUrls.has(normalizeMCPUrl(entry.url))
      );
    }, [mcpEnabled, catalogEntries, connectedMCPUrls]);

    const totalUnconnected =
      inactiveIntegrations.length + unconnectedCatalog.length;

    if (isDismissed || totalUnconnected === 0 || isOutOfCreditsBlocking()) {
      return null;
    }

    const handleBannerClick = () => {
      ModalsManager.show(AddIntegrationsModal, {});
    };

    const handleDismiss = (e: React.MouseEvent) => {
      e.stopPropagation();
      chatStore.setConnectIntegrationsBannerDismissed(true);
    };

    const maxBadges = Math.max(
      0,
      Math.floor((bannerWidth - BADGE_RESERVED_PX) / BADGE_STEP_PX)
    );

    return (
      <ComposerBanner
        side="bottom"
        ref={bannerRef}
        onClick={handleBannerClick}
        className={cn(
          'pr-3',
          'text-xs text-foreground-muted',
          'hover:bg-[color-mix(in_srgb,var(--foreground-default)_8%,transparent)]',
          'transition-colors',
          className
        )}
      >
        <XStack className="items-center justify-between gap-4">
          <span className="text-xs text-foreground-muted truncate min-w-0">
            Connect your apps to get better answers
          </span>
          <XStack className="items-center gap-3 shrink-0">
            {inactiveIntegrations.slice(0, maxBadges).map((state) => {
              const IntegrationIcon = getIntegrationIcon(
                state.info.toolkit_name
              );
              return (
                <IntegrationIcon
                  key={state.info.toolkit_name}
                  className="size-4"
                />
              );
            })}
            {unconnectedCatalog
              .slice(0, Math.max(0, maxBadges - inactiveIntegrations.length))
              .map((entry) => {
                if (entry.icon_url) {
                  return (
                    <img
                      key={entry.id}
                      src={entry.icon_url}
                      alt=""
                      className="size-4 rounded object-contain shrink-0"
                    />
                  );
                }
                const CatalogIcon = getIntegrationIcon(entry.id);
                return <CatalogIcon key={entry.id} className="size-4" />;
              })}
            <button
              onClick={handleDismiss}
              className={cn(
                'p-0.5 rounded hover:bg-secondary-active',
                'transition-colors',
                'flex items-center justify-center'
              )}
              aria-label="Dismiss banner"
            >
              <Icons.cancel className="size-3.5 text-foreground-subtle" />
            </button>
          </XStack>
        </XStack>
      </ComposerBanner>
    );
  }
);

const belowTextboxRecommendationPresentation = {
  containerPosition: 'below_textbox',
  size: 'sm',
  textClass: 'prose prose-bird prose-sm text-foreground normal-case',
} as const;

const Recommendations = observer(() => {
  const recommendations = useNewChatSuggestions();
  const { setHash } = useHashRoute();

  const recommendationSetGeneratedAtRef = useRef(new Date().toISOString());

  const onClick = (suggestion: string, positionInList: number) => {
    trackEvent('RECOMMENDATION_CLICKED', {
      suggestion,
      recommendation_set_generated_at: recommendationSetGeneratedAtRef.current,
      container_position:
        belowTextboxRecommendationPresentation.containerPosition,
      position_in_list: positionInList,
      size: belowTextboxRecommendationPresentation.size,
    });
    localChatEventsBus.emit(COMPOSER_START_NEW_THREAD, {
      data: {
        message: suggestion,
      },
    });
  };

  const recommendationsKey = recommendations.join('|');

  // Track impressions only when the displayed set changes. The inactivity
  // refresh usually re-applies an identical set, and firing on every refresh
  // cycle floods PostHog from idle machines.
  useEffect(() => {
    if (recommendations.length === 0) return;

    const recommendationSetGeneratedAt = new Date().toISOString();
    recommendationSetGeneratedAtRef.current = recommendationSetGeneratedAt;
    recommendations.forEach((suggestion, index) => {
      trackEvent('RECOMMENDATION_SHOWN', {
        suggestion,
        recommendation_set_generated_at: recommendationSetGeneratedAt,
        container_position:
          belowTextboxRecommendationPresentation.containerPosition,
        position_in_list: index + 1,
        size: belowTextboxRecommendationPresentation.size,
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendationsKey]);

  return (
    <div className="relative w-full">
      <AnimatePresence mode="popLayout">
        <motion.div
          key={recommendationsKey}
          className="mx-auto flex w-full flex-wrap items-center justify-center gap-2"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.08,
                when: 'beforeChildren',
              },
            },
          }}
        >
          <AnimatePresence mode="popLayout">
            {recommendations.map((suggestion, index) => (
              <RecommendationButton
                key={suggestion}
                suggestion={suggestion}
                onClick={() => onClick(suggestion, index + 1)}
              >
                {suggestion}
              </RecommendationButton>
            ))}
            <RecommendationButton
              suggestion="More suggestions"
              onClick={() => {
                trackEvent('CHAT_PROMPT_LIBRARY_OPENED');
                setHash('chat-library');
              }}
              className="inline-flex items-center gap-2"
            >
              <Icons.sparkles className="size-3.5" /> More suggestions
            </RecommendationButton>
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
});

const RecommendationButton = ({
  suggestion,
  onClick,
  className,
  children,
}: {
  suggestion: string;
  onClick: (suggestion: string) => void;
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <motion.button
      type="button"
      className={cn(
        'cursor-default',
        belowTextboxRecommendationPresentation.textClass,
        'bg-surface border border-border rounded-xl',
        'py-1 px-3 whitespace-nowrap',
        'transition-colors duration-100',
        'hover:bg-surface-hover',
        'focus-ring no-record',
        className
      )}
      layout
      layoutId={`recommendation-${suggestion}`}
      variants={{
        hidden: {
          opacity: 0,
          y: 15,
          scale: 0.95,
          filter: 'blur(2px)',
        },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 24,
          },
        },
      }}
      exit={{
        opacity: 0,
        y: -10,
        scale: 0.9,
        filter: 'blur(2px)',
        transition: {
          duration: 0.2,
        },
      }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(suggestion)}
    >
      {children}
    </motion.button>
  );
};
